import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, XCircle, ChevronRight, FileText } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";
import Logo from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccessPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token, refreshUser } = useAuth();
  const [status, setStatus] = useState("checking");
  const [metadata, setMetadata] = useState(null);
  const [countdown, setCountdown] = useState(5);

  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (!sessionId || !token) { setStatus("failed"); return; }
    let attempts = 0;
    const pollStatus = async () => {
      if (attempts >= 8) { setStatus("failed"); return; }
      try {
        const res = await axios.get(`${API}/checkout/status/${sessionId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.data.payment_status === "paid") {
          setStatus("success");
          setMetadata(res.data.metadata);
          await refreshUser();
          return;
        } else if (res.data.status === "expired") { setStatus("failed"); return; }
        attempts++;
        setTimeout(pollStatus, 2000);
      } catch { attempts++; setTimeout(pollStatus, 2000); }
    };
    pollStatus();
  }, [sessionId, token, refreshUser]);

  // Auto-redirect countdown
  useEffect(() => {
    if (status !== "success") return;
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          navigate(metadata?.place_id ? `/report/${metadata.place_id}` : "/dashboard");
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [status, metadata, navigate]);

  const placeId = metadata?.place_id;

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-white" data-testid="payment-success-page">
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="w-full max-w-lg">
        <Card className="bg-white border-slate-200 shadow-lg" style={{ borderRadius: 16 }}>
          <CardContent className="p-8 text-center">
            <div className="flex justify-center mb-6">
              <Logo size="large" theme="light" />
            </div>

            {status === "checking" && (
              <>
                <Loader2 className="w-16 h-16 animate-spin mx-auto mb-4" style={{ color: "#6366f1" }} />
                <h2 className="text-2xl font-bold text-[#1e293b] mb-2">Processing Payment...</h2>
                <p className="text-[#64748b]">Please wait while we confirm your payment.</p>
              </>
            )}

            {status === "success" && (
              <>
                {/* Animated green checkmark */}
                <motion.div
                  initial={{ scale: 0 }} animate={{ scale: 1 }}
                  transition={{ type: "spring", stiffness: 200, delay: 0.2 }}
                  className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4"
                  style={{ background: "#22c55e" }}
                >
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3">
                    <motion.path
                      initial={{ pathLength: 0 }} animate={{ pathLength: 1 }}
                      transition={{ duration: 0.5, delay: 0.5 }}
                      strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"
                    />
                  </svg>
                </motion.div>

                <h2 className="text-2xl font-bold text-[#1e293b] mb-2">Payment Successful!</h2>
                <p className="text-[#64748b] mb-2">Your full report is unlocked</p>
                <p className="text-sm text-[#94a3b8] mb-6">Check your email for a copy of your complete audit report</p>

                <Button
                  onClick={() => navigate(placeId ? `/report/${placeId}` : "/dashboard")}
                  className="font-bold text-white px-8 h-12"
                  style={{ background: "#6366f1" }}
                  data-testid="view-report-btn"
                >
                  <FileText className="w-4 h-4 mr-2" />View My Full Report &rarr;
                </Button>

                <p className="text-xs text-[#94a3b8] mt-3">
                  Taking you to your report in <strong>{countdown}</strong>...
                </p>

                {/* Upsell CTA */}
                <div className="rounded-xl p-5 mt-6 text-left" style={{ background: "#0f172a" }}>
                  <p className="font-bold text-sm text-white mb-1">Want us to fix everything?</p>
                  <p className="text-xs text-slate-400 mb-3">
                    Upgrade to a monthly plan and get expert implementation of all fixes.
                  </p>
                  <a
                    href="https://calendly.com/rankifylocal"
                    target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs font-bold"
                    style={{ color: "#6366f1" }}
                  >
                    Book a Free Strategy Call <ChevronRight className="w-3 h-3" />
                  </a>
                </div>
              </>
            )}

            {status === "failed" && (
              <>
                <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <XCircle className="w-12 h-12 text-[#ef4444]" />
                </div>
                <h2 className="text-2xl font-bold text-[#1e293b] mb-2">Payment Issue</h2>
                <p className="text-[#64748b] mb-6">
                  We couldn't confirm your payment. If you were charged, please contact support.
                </p>
                <Button
                  onClick={() => navigate("/")}
                  className="font-bold text-white px-8"
                  style={{ background: "#6366f1" }}
                >
                  Back to Home
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
